import { signIn as amplifySignIn, signOut as amplifySignOut, getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import useUser from '@/hooks/useUser';
import { VALIDATION_MESSAGES } from '@/lib/constants/validation';
import { useOrgsStore } from '@/stores/orgs';
import { useUiStore } from '@/stores/ui';
import { useUserStore } from '@/stores/user';

export default function useAuth() {
  const navigate = useNavigate();

  async function isAuthed() {
    try {
      const authenticatedUser = await getCurrentUser();
      return !!authenticatedUser;
    } catch (error) {
      console.error('Error occurred getting the authenticated user.', error);
      throw error;
    }
  }

  async function signIn(username: string, password: string) {
    try {
      useUiStore.getState().setRequestPending('signIn');
      await amplifySignOut().catch(() => {});
      const result = await amplifySignIn({ username, password });
      if (result.isSignedIn) {
        await useUser().setCurrentUserDataFromToken();
        await useOrgsStore.getState().loadOrgs();
        void navigate('/');
      }
    } catch (error: any) {
      if (error.name === 'NotAuthorizedException') {
        toast.error('Incorrect email or password. Please try again.');
      } else {
        toast.error(VALIDATION_MESSAGES.network);
      }
      console.error('Error occurred during sign in.', error);
      throw error;
    } finally {
      useUiStore.getState().setRequestComplete('signIn');
    }
  }

  async function getToken(): Promise<string> {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    if (!token) {
      throw new Error('No auth token available');
    }
    return token;
  }

  async function getRefreshedToken(): Promise<string> {
    try {
      const session = await fetchAuthSession({ forceRefresh: true });
      const token = session.tokens?.idToken?.toString();
      if (!token) {
        throw new Error('No auth token available after refresh');
      }
      return token;
    } catch (error) {
      console.error('Error occurred during token refresh.', error);
      throw error;
    }
  }

  async function signOut() {
    try {
      await amplifySignOut();
      useUserStore.getState().reset();
    } catch (error) {
      console.error('Error occurred during sign out.', error);
      throw error;
    }
  }

  async function signOutAndRedirect() {
    await signOut();
    toast.success('You have been signed out.');
    void navigate('/signin');
  }

  return {
    getToken,
    getRefreshedToken,
    isAuthed,
    signIn,
    signOut,
    signOutAndRedirect,
  };
}
